import React from 'react';
import {connect} from 'react-redux';

import {selectSort, setSort, SORTING} from 'ducks/sorting';
import {getNextDirectionSort, EMPTY_SORT_OBJECT, SORT_NONE} from 'utils/sort';
import {getSortFromUrl} from 'utils/sort';
import {storeSortInUrl} from 'ducks/urls';
import {composeActions} from 'ducks/common';
import {ExtraAction} from 'ducks/types';

const isSortEmpty = sortObject => {
  return sortObject.direction === SORT_NONE;
};

/*
  options: {
    saveToUrl:
      Whether or not to save the sorting in the url when it changes.
      The url is cleared on unmount.
      If saveToUrl is `true` and If any sorting state is found in the store for this component on mounting: 
        The url will be updated with the sort from the store on component mount.

    clearSortingOnUnmount:
      Whether or not to leave the sort objects in the store after unmounting the component so
      they would be remembered for the next time that the component will mount.
  }
*/

export interface WithSortingProps {
  onSort: (field: string, extraActions?: ExtraAction[]) => void;
  getSort: () => ReturnType<typeof selectSort>;
}

const defaultOptions = {saveToUrl: false, clearSortingOnUnmount: true};

const withSorting = <T extends any>(
  Component: React.ComponentType<T & WithSortingProps>,
  namespace: string,
  options: Partial<typeof defaultOptions> = {}
) => {
  type WithSortingWrapperProps = T &
    typeof mapDispatchToProps &
    ReturnType<typeof mapStateToProps>;

  const combinedOptions = {...defaultOptions, ...options}; // TODO: fix

  class WrappedComponent extends React.Component<WithSortingWrapperProps> {
    componentDidMount() {
      if (combinedOptions.saveToUrl) {
        const sortFromUrl = getSortFromUrl();

        if (!isSortEmpty(sortFromUrl)) {
          this.props.setSort({namespace, sortForStore: sortFromUrl});
        }

        // Check the store for an existing sort object for this namespace
        if (isSortEmpty(sortFromUrl) && !isSortEmpty(this.props.sortInStore)) {
          // If it is in the store but not the url - set it in the url as well.
          this.props.storeSortInUrl({
            sortSelector: state => selectSort(state, namespace)
          });
        }

        // If it cannot be found in the url or the store - create a new empty sort object for this namespace
        if (isSortEmpty(sortFromUrl) && isSortEmpty(this.props.sortInStore)) {
          this.props.setSort({namespace, sortForStore: EMPTY_SORT_OBJECT});
        }
      } else {
        this.props.setSort({namespace, sortForStore: EMPTY_SORT_OBJECT});
      }
    }

    componentWillUnmount() {
      if (combinedOptions.clearSortingOnUnmount) {
        // clear sort from the store
        this.props.setSort({namespace, sortForStore: EMPTY_SORT_OBJECT});
      }

      // if the url was used for sorting, clear it on component unmount
      if (combinedOptions.saveToUrl) {
        this.props.storeSortInUrl({
          sortSelector: () => EMPTY_SORT_OBJECT
        });
      }
    }

    onSort = (field: string, extraActions: ExtraAction[] = []) => {
      const sort = getNextDirectionSort(field, this.props.sortInStore);

      if (combinedOptions.saveToUrl) {
        this.props.composeActions([
          () => setSort({namespace, sortForStore: sort}),
          () =>
            storeSortInUrl({
              sortSelector: state => selectSort(state, namespace)
            }),
          ...extraActions
        ]);
      } else {
        this.props.composeActions([
          () => setSort({namespace, sortForStore: sort}),
          ...extraActions
        ]);
      }
    };

    getSort = () => this.props.sortInStore;

    render() {
      // This downcast is important to remove the added props of the WrappedComponent
      const restOfProps: T = this.props;

      return (
        <Component
          {...restOfProps}
          onSort={this.onSort}
          getSort={this.getSort}
        />
      );
    }
  }

  const mapStateToProps = state => {
    return {
      sortInStore: selectSort(state, namespace)
    };
  };

  const mapDispatchToProps = {
    setSort,
    composeActions,
    storeSortInUrl
  };

  return connect(mapStateToProps, mapDispatchToProps)(
    WrappedComponent as React.ComponentType<T>
  );
};

// NOTE: `SORTING` is exported here only for convenience sake
export {withSorting, SORTING};
